import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SearchBox from '@/components/base/SearchBox';
import SiteCard from '@/components/base/SiteCard';
import EmptyState from '@/components/base/EmptyState';
import { useSeo } from '@/hooks/useSeo';
import { categories } from '@/mocks/navData';
import { getCategoryBySlug, getHotSites, searchSites } from '@/utils/nav';

export default function Search() {
  const [params] = useSearchParams();
  const keyword = params.get('q') || '';
  const [type, setType] = useState('全部');

  useSeo({
    title: keyword ? `${keyword} - 搜索结果 - 蓝导航` : '搜索 - 蓝导航',
    description: keyword
      ? `蓝导航中与「${keyword}」相关的网站搜索结果，快速找到你需要的网站入口。`
      : '在蓝导航中搜索网站、分类或关键词，快速定位你需要的网站入口。',
    keywords: keyword
      ? `蓝导航,${keyword},网站搜索,导航搜索,快捷入口`
      : '蓝导航,网站搜索,导航搜索',
  });

  useEffect(() => {
    setType('全部');
  }, [keyword]);

  const results = useMemo(() => searchSites(keyword), [keyword]);

  const typeOptions = useMemo(() => {
    const present = categories.filter((category) =>
      results.some((site) => site.category === category.slug),
    );
    return ['全部', ...present.map((category) => category.label)];
  }, [results]);

  const filtered = useMemo(() => {
    if (type === '全部') return results;
    return results.filter(
      (site) => getCategoryBySlug(site.category)?.label === type,
    );
  }, [results, type]);

  return (
    <div className="w-full">
      <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
        <h1 className="font-heading text-2xl font-extrabold text-foreground-950">
          搜索导航
        </h1>
        <p className="mt-1 text-sm text-foreground-600">
          输入关键词，快速查找新闻、视频、购物、社交等领域的网站入口。
        </p>
        <div className="mt-4 max-w-2xl">
          <SearchBox defaultValue={keyword} size="lg" />
        </div>
      </div>

      {!keyword ? (
        <div className="mt-6">
          <EmptyState
            icon="ri-search-eye-line"
            title="输入关键词开始搜索"
            description="试试搜索「购物」「视频」或某个网站名称，例如「淘宝」。"
            action={{ to: '/', label: '返回首页浏览分类' }}
          />
        </div>
      ) : (
        <>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-600">
              关键词
              <strong className="mx-1 font-heading font-semibold text-primary-600">
                “{keyword}”
              </strong>
              共找到
              <strong className="mx-1 font-semibold text-foreground-950">
                {results.length}
              </strong>
              个结果
            </p>
            <span className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-500">
              演示搜索 · 前端关键词匹配
            </span>
          </div>

          {results.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {typeOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setType(option)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                    type === option
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-600'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          )}

          {filtered.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((site) => (
                <SiteCard key={site.id} site={site} />
              ))}
            </div>
          ) : (
            <div className="mt-6">
              <EmptyState
                icon="ri-emotion-sad-line"
                title="没有找到相关网站"
                description={`暂时没有与「${keyword}」匹配的导航结果，换个关键词试试，或看看下面的热门导航推荐。`}
              />

              <section className="mt-8">
                <h2 className="mb-4 font-heading text-lg font-bold text-foreground-950">
                  热门导航推荐
                </h2>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {getHotSites(6).map((site) => (
                    <SiteCard key={site.id} site={site} />
                  ))}
                </div>
              </section>
            </div>
          )}
        </>
      )}
    </div>
  );
}